<template>
  <WdsStack class="tw-justify-between" gap-sm align-center>
    <WdsStack gap-md>
      <WdsButton
        tertiary
        :disabled="filtersDisabled"
        :class="{ 'filters-active': filtersAreOpened }"
        @click="filtersAreOpened = !filtersAreOpened"
      >
        <div class="tw-my-auto">
          <WdsStack gap-sm align-center>
            <WdsIcon name="settings-adjust" :size="16" />
            <WdsText>
              {{ $t('Filter') }}
            </WdsText>
          </WdsStack>
        </div>
      </WdsButton>
      <WdsStack v-if="filtersAreOpened" gap-md>
        <WdsSelect
          v-model="billWorkerStatus.data"
          :list="$options.statusOptions"
          :placeholder="$t('Status')"
          class="tw-w-[200px]"
          can-deselect
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <WdsSelect
          v-model="selectedClients.data"
          multi-select
          can-deselect
          searchable
          :list="clients"
          :placeholder="$t('Clients')"
          class="tw-w-[200px]"
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
        <WdsSelect
          v-model="paymentRequestJobTitles.data"
          multi-select
          can-deselect
          searchable
          :list="jobTitles"
          :placeholder="$t('Job Titles')"
          class="tw-w-[200px]"
        >
          <template #option="{ option }">
            <div>
              {{ option.label }}
            </div>
          </template>
        </WdsSelect>
      </WdsStack>
    </WdsStack>
    <WdsStack v-if="!viewerIsStaffingAgency">
      <a
        href="https://help.worksome.dk/en/articles/10026605-when-will-i-get-paid"
        target="_blank"
        class="tw-ml-auto"
      >
        {{ $t('When will I get paid?') }}
      </a>
    </WdsStack>
  </WdsStack>
</template>

<script>
import { WdsIcon, WdsText, WdsStack, WdsSelect, WdsButton } from '@worksome/wds-components'
import { inject } from 'vue'
import { $t } from '@/composables/translation'
import { LocalStorageEncoder } from '@/utils/Encoders'
import { PaymentRequestWorkerStatus } from '@/gql/graphql'
import { getWorkerStatusLabel } from '@/components/PaymentRequest/utilities/statusFormatting'

export default {
  setup() {
    const viewerIsStaffingAgency = inject('viewerIsStaffingAgency')
    return { viewerIsStaffingAgency }
  },
  statusOptions: [
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Draft),
      value: PaymentRequestWorkerStatus.Draft,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Requested),
      value: PaymentRequestWorkerStatus.Requested,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Approved),
      value: PaymentRequestWorkerStatus.Approved,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Processing),
      value: PaymentRequestWorkerStatus.Processing,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Completed),
      value: PaymentRequestWorkerStatus.Completed,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Rejected),
      value: PaymentRequestWorkerStatus.Rejected,
    },
    {
      label: getWorkerStatusLabel(PaymentRequestWorkerStatus.Cancelled),
      value: PaymentRequestWorkerStatus.Cancelled,
    },
  ],
  components: {
    WdsButton,
    WdsSelect,
    WdsText,
    WdsIcon,
    WdsStack,
  },
  props: {
    filtersDisabled: {
      type: Boolean,
      default: false,
    },
    clients: {
      type: Array,
      default: () => [],
    },
    jobTitles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      filtersAreOpened: false,
      selectedClients: new LocalStorageEncoder({
        storageKey: 'clients',
        decoder: this.decodeClientOptionsFromUrl,
        filter: (statuses) => statuses.map((status) => status.value),
        defaultVal: null,
      }),
      paymentRequestJobTitles: new LocalStorageEncoder({
        storageKey: 'job-titles',
        decoder: this.decodeJobTitlesOptionsFromUrl,
        filter: (statuses) => statuses.map((status) => status.value),
        defaultVal: null,
      }),
      billWorkerStatus: new LocalStorageEncoder({
        storageKey: 'worker-status',
        decoder: this.decodeStatusOptionsFromUrl,
        filter: (statuses) => statuses.value,
        defaultVal: null,
      }),
    }
  },
  computed: {
    filter() {
      return {
        workerStatus: this.billWorkerStatus.data?.value,
        jobTitles: this.paymentRequestJobTitles.data.map((status) => status.value),
        companies: this.selectedClients.data.map((status) => status.value),
      }
    },
  },
  watch: {
    filter: {
      immediate: true,
      deep: true,
      handler(newVal) {
        this.$emit('filters-updated', newVal)
      },
    },
  },
  methods: {
    $t,
    decodeStatusOptionsFromUrl(data) {
      return this.$options.statusOptions?.filter((option) => data === option.value)
    },
    decodeClientOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.clients?.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
    decodeJobTitlesOptionsFromUrl(data) {
      const selections = data?.split(',').filter(Boolean) || []
      return this.jobTitles?.filter((option) =>
        selections.some((selection) => parseInt(selection, 10) === option.value),
      )
    },
  },
}
</script>
